import { useCallback } from "react";
import { useApolloClient } from "@apollo/client/react";
import { toast } from "sonner";
import { extractNodesAndPageInfo } from "@/lib/utils/export/csv-export";
import type { GraphqlCollectionResponse } from "@/lib/utils/export/csv-export/summary";
import {
  buildPresetUserFilter,
  mergeFiltersWithAnd,
  resolveCurrentUserFilterTokens,
} from "@/lib/utils/crm/user-filter-preset";
import type { BaseEntity, EntityHooks } from "../../UniversalCRMTable/types";
import type { EntityListData, GraphQLFilter } from "../types";
import { errorMessage } from "@/lib/utils/api/error-text";

interface UseFetchAllPagesParams<T extends BaseEntity> {
  entityName: string;
  hooks: EntityHooks<T>;
  filterPreset: "user" | "all";
  appUser: { id: string } | null | undefined;
  debouncedSearchTerm: string;
  customFilters: GraphQLFilter;
  currentFilters: GraphQLFilter;
  advancedFilters: GraphQLFilter;
  data: EntityListData<T> | undefined;
  convertToGraphQLFilter: (filters: GraphQLFilter) => GraphQLFilter;
  constructSearchFilter: (searchTerm: string) => GraphQLFilter;
  convertAdvancedFiltersToGraphQL: (advancedFilters: GraphQLFilter) => GraphQLFilter;
  isFetchingRef: React.MutableRefObject<boolean>;
  setExportProgress: (progress: number) => void;
}

export function useFetchAllPages<T extends BaseEntity>({
  entityName,
  hooks,
  filterPreset,
  appUser,
  debouncedSearchTerm,
  customFilters,
  currentFilters,
  advancedFilters,
  data,
  convertToGraphQLFilter,
  constructSearchFilter,
  convertAdvancedFiltersToGraphQL,
  isFetchingRef,
  setExportProgress,
}: UseFetchAllPagesParams<T>): () => Promise<T[]> {
  const apolloClient = useApolloClient();

  return useCallback(async (): Promise<T[]> => {
    const hasActiveFilters =
      Object.keys(currentFilters).length > 0 ||
      Object.keys(advancedFilters).length > 0 ||
      debouncedSearchTerm.length > 0;

    if (isFetchingRef.current) return [];
    isFetchingRef.current = true;

    try {
      setExportProgress(10);

      const allData: T[] = [];
      let cursor: string | null = null;
      let pageCount = 0;
      const maxPages = 10000;
      const pageSize = 30;

      if (!hooks.queryDocument) {
        console.error("❌ Query document not available for pagination:", { hooks: Object.keys(hooks) });
        throw new Error(
          "Query document not available for pagination. Export requires GraphQL hooks with queryDocument."
        );
      }

      if (data?.data && data.data.length > 0) {
        allData.push(...data.data);
        pageCount = 1;
      }

      const totalCount = data?.totalCount || 0;
      const currentPageSize = data?.data?.length || 0;
      let hasMorePages = data?.pageInfo?.hasNextPage || false;

      if (process.env.NODE_ENV === "development") {
        console.log("🔍 Starting export with pagination:", {
          totalCount,
          currentPageSize,
          hasMorePages,
          hasActiveFilters,
          startCursor: data?.pageInfo?.endCursor,
        });
      }

      if (hasMorePages && data?.pageInfo?.endCursor) {
        cursor = data.pageInfo.endCursor;

        while (cursor && pageCount < maxPages && allData.length < totalCount) {
          pageCount++;

          const fetchProgress = 10 + Math.min(40, Math.floor((allData.length / totalCount) * 40));
          setExportProgress(Math.min(fetchProgress, 50));

          if (process.env.NODE_ENV === "development") {
            console.log(`🔍 Fetching page ${pageCount} with cursor: ${cursor?.substring(0, 20)}...`);
            console.log(`🔍 Current progress: ${allData.length} / ${totalCount} items`);
          }

          try {
            const baseFilter = {
              ...constructSearchFilter(debouncedSearchTerm),
              ...convertToGraphQLFilter(customFilters),
              ...convertToGraphQLFilter(currentFilters),
              ...convertAdvancedFiltersToGraphQL(advancedFilters),
            };

            const resolvedBaseFilter = resolveCurrentUserFilterTokens(baseFilter, appUser?.id);

            const scopedFilter =
              filterPreset === "user" && appUser?.id
                ? mergeFiltersWithAnd(
                    resolvedBaseFilter,
                    buildPresetUserFilter({ entityName, userId: appUser.id })
                  )
                : resolvedBaseFilter;

            if (!hooks.queryDocument) throw new Error("Query document not available");

            // Explicit annotation breaks the TS7022 self-referential inference on
            // Apollo's generic `query` overload; we only read `data` downstream.
            const response: { data?: GraphqlCollectionResponse<T> | null } =
              await apolloClient.query<GraphqlCollectionResponse<T>>({
                query: hooks.queryDocument,
                variables: { first: pageSize, after: cursor, filter: scopedFilter },
                fetchPolicy: "network-only",
              });

            const { nodes, pageInfo } = extractNodesAndPageInfo<T>(response?.data);

            if (process.env.NODE_ENV === "development") {
              console.log(`🔍 Page ${pageCount} response:`, {
                nodesCount: nodes?.length || 0,
                hasNextPage: pageInfo?.hasNextPage,
                endCursor: pageInfo?.endCursor?.substring(0, 20),
                currentTotal: allData.length,
                expectedTotal: totalCount,
              });
            }

            if (nodes && nodes.length > 0) {
              allData.push(...nodes);
            } else {
              if (allData.length < totalCount && pageInfo?.hasNextPage && cursor) {
                // hasNextPage true but 0 items — continue
              } else {
                break;
              }
            }

            hasMorePages = pageInfo?.hasNextPage || false;
            cursor = pageInfo?.endCursor || null;

            if (allData.length >= totalCount || !cursor) break;
          } catch (pageError) {
            if (process.env.NODE_ENV === "development") {
              console.log("🔍 Error fetching page:", pageError);
            }
            break;
          }
        }
      }

      setExportProgress(50);

      if (allData.length < totalCount) {
        if (hasActiveFilters) {
          toast.info(`Exported ${allData.length} of ${totalCount} filtered items.`, { duration: 5000 });
        } else {
          toast.info(`Exported ${allData.length} of ${totalCount} items.`, { duration: 5000 });
        }
      } else {
        toast.info(`Exported all ${allData.length} items.`, { duration: 3000 });
      }

      return allData;
    } catch (error) {
      console.error("🔍 Export Error:", error);
      toast.error("Failed to export: " + errorMessage(error));
      throw error;
    } finally {
      isFetchingRef.current = false;
    }
  }, [
    data?.totalCount,
    data?.data,
    data?.pageInfo,
    currentFilters,
    advancedFilters,
    debouncedSearchTerm,
    filterPreset,
    appUser?.id,
    customFilters,
    constructSearchFilter,
    convertToGraphQLFilter,
    convertAdvancedFiltersToGraphQL,
    hooks,
    apolloClient,
    isFetchingRef,
    setExportProgress,
    entityName,
  ]);
}
